"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import type { User as FirebaseUser } from "firebase/auth";
import {
  onAuthStateChanged,
  signInWithPopup,
  signOut,
} from "firebase/auth";
import { getFirebaseAuth, googleProvider } from "@/lib/firebase";
import { clearLegacySessionCookie } from "@/lib/session";

export interface AuthUser {
  uid: string;
  id: string;
  name: string;
  email: string;
  avatarUrl?: string;
  preferredLocale?: string;
}

interface AuthContextValue {
  user: AuthUser | null;
  loading: boolean;
  signingIn: boolean;
  error: AuthError | null;
  signInWithGoogle: () => Promise<void>;
  logout: () => Promise<void>;
  getIdToken: () => Promise<string | undefined>;
}

type AuthError = "unauthorized" | "network" | "error";

type AccountValidationResult =
  | { account: { id: string; preferredLocale?: string }; error?: never }
  | { account?: never; error: AuthError };

const AuthContext = createContext<AuthContextValue | null>(null);

function mapFirebaseUser(fbUser: FirebaseUser): Omit<AuthUser, "id"> {
  return {
    uid: fbUser.uid,
    name: fbUser.displayName ?? fbUser.email ?? "User",
    email: fbUser.email ?? "",
    avatarUrl: fbUser.photoURL ?? undefined,
  };
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [signingIn, setSigningIn] = useState(false);
  const [error, setError] = useState<AuthError | null>(null);

  const validateAccount = useCallback(
    async (token: string): Promise<AccountValidationResult> => {
      let res: Response;
      try {
        const baseUrl =
          import.meta.env.VITE_API_URL ?? "http://localhost:3000";
        res = await fetch(`${baseUrl}/account`, {
          headers: { Authorization: `Bearer ${token}` },
        });
      } catch {
        return { error: "network" };
      }

      if (res.status === 401 || res.status === 403) {
        return { error: "unauthorized" };
      }
      if (!res.ok) return { error: "error" };

      try {
        const body: unknown = await res.json();
        if (
          !body ||
          typeof body !== "object" ||
          typeof (body as { id?: unknown }).id !== "string"
        ) {
          return { error: "error" };
        }
        const account = body as {
          id: string;
          preferred_locale?: unknown;
        };
        return {
          account: {
            id: account.id,
            preferredLocale:
              typeof account.preferred_locale === "string"
                ? account.preferred_locale
                : undefined,
          },
        };
      } catch {
        return { error: "error" };
      }
    },
    [],
  );

  const handleAuthUser = useCallback(
    async (fbUser: FirebaseUser | null) => {
      if (!fbUser) {
        setUser(null);
        setLoading(false);
        return;
      }

      try {
        const token = await fbUser.getIdToken();
        const result = await validateAccount(token);
        if (result.error) {
          if (result.error === "unauthorized") {
            await signOut(getFirebaseAuth());
          }
          setUser(null);
          setError(result.error);
          setLoading(false);
          return;
        }

        setUser({
          ...mapFirebaseUser(fbUser),
          id: result.account.id,
          preferredLocale: result.account.preferredLocale,
        });
        setError(null);
      } catch {
        setUser(null);
        setError("error");
      } finally {
        setLoading(false);
      }
    },
    [validateAccount],
  );

  useEffect(() => {
    clearLegacySessionCookie();
    const unsubscribe = onAuthStateChanged(getFirebaseAuth(), handleAuthUser);
    return unsubscribe;
  }, [handleAuthUser]);

  const signInWithGoogle = useCallback(async () => {
    if (signingIn) return;
    setError(null);
    setSigningIn(true);
    try {
      const currentUser = getFirebaseAuth().currentUser;
      if (currentUser) {
        await handleAuthUser(currentUser);
        return;
      }
      await signInWithPopup(getFirebaseAuth(), googleProvider);
    } catch (err: unknown) {
      const code = (err as { code?: string }).code;
      if (code === "auth/popup-closed-by-user") return;
      if (code === "auth/network-request-failed") {
        setError("network");
      } else {
        setError("error");
      }
    } finally {
      setSigningIn(false);
    }
  }, [handleAuthUser, signingIn]);

  const logout = useCallback(async () => {
    await signOut(getFirebaseAuth());
    clearLegacySessionCookie();
    setUser(null);
    setError(null);
  }, []);

  const getIdToken = useCallback(async () => {
    return getFirebaseAuth().currentUser?.getIdToken();
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        signingIn,
        error,
        signInWithGoogle,
        logout,
        getIdToken,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
